import React, { useEffect, useState } from "react";
import { api, downloadAuthFile, formatApiError, resolveImg } from "../../lib/api";
import { Pencil, Trash2, Plus, Download, Upload, Copy, Image as ImageIcon, Languages } from "lucide-react";
import { toast } from "sonner";
import BlockImageOverlayEditor from "../../components/BlockImageOverlayEditor";
import HighlightsPicker from "../../components/HighlightsPicker";

const empty = {
  slug: "",
  name: "",
  name_ar: "",
  brand: "Comfee",
  category: "Split AC",
  price: 0,
  sale_price: null,
  description: "",
  description_ar: "",
  images: [""],
  specs: { btu: "", type: "split", energy_rating: "A+", color: "White", warranty: "3 years" },
  stock: 0,
  featured: false,
  on_sale: false,
  quote_only: false,
  spec_pdf_url: "",
  manual_pdf_url: "",
  long_description: [],
  variants: [],
  highlights: [],
  home_sections: [],
  home_section_orders: {},
};

const SIZE_PRESETS = [
  { label: "1 Ton", btu: "12000" },
  { label: "1.5 Ton", btu: "18000" },
  { label: "2 Ton", btu: "24000" },
  { label: "2.5 Ton", btu: "30000" },
  { label: "3 Ton", btu: "36000" },
];

const slugifyVariant = (slug, btu, label) =>
  `${slug || "p"}-${btu || label}`.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | id
  const [form, setForm] = useState(empty);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);

  const refresh = () => api.get("/admin/products").then((r) => setProducts(r.data));

  useEffect(() => {
    refresh();
    // `refresh` is a stable fetch helper defined in-scope; adding it to deps
    // would require useCallback and gains nothing — we only want to run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One-click EN → AR translation via /api/admin/translate (Gemini)
  const [translating, setTranslating] = useState("");
  const translateField = async (sourceKey, targetKey, context = "") => {
    const src = (form[sourceKey] || "").trim();
    if (!src) {
      toast.error("Please fill the English field first.");
      return;
    }
    setTranslating(targetKey);
    try {
      const { data } = await api.post("/admin/translate", { text: src, context });
      if (data?.text) {
        setForm((prev) => ({ ...prev, [targetKey]: data.text }));
        toast.success("Translated to Arabic");
      } else {
        toast.error("Empty translation returned");
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Translation failed");
    } finally {
      setTranslating("");
    }
  };

  const startNew = () => {
    setEditing("new");
    setForm(empty);
    setError("");
  };

  // Bulk auto-translate every product's missing Arabic fields (name, description, rich blocks)
  const [translatingAll, setTranslatingAll] = useState(false);
  const translateAllMissing = async () => {
    if (translatingAll) return;
    if (!window.confirm("Scan all products and auto-translate any missing Arabic fields? This may take a minute.")) return;
    setTranslatingAll(true);
    const t = toast.loading("Translating missing Arabic fields…");
    try {
      const { data } = await api.post("/admin/translate-missing");
      toast.success(
        `Translated ${data.translated_fields} field(s) across ${data.updated_products}/${data.scanned} products`,
        { id: t }
      );
      if (data.errors?.length) {
        toast.error(`${data.errors.length} product(s) had errors — check server logs`);
      }
      refresh();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Bulk translation failed", { id: t });
    } finally {
      setTranslatingAll(false);
    }
  };

  const startEdit = (p) => {
    setEditing(p.id);
    setForm({
      ...p,
      specs: { ...empty.specs, ...(p.specs || {}) },
      images: p.images?.length ? p.images : [""],
      variants: p.variants || [],
    });
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = {
        ...form,
        name_ar: form.name_ar?.trim() || null,
        description_ar: form.description_ar?.trim() || null,
        price: form.quote_only ? 0 : Number(form.price || 0),
        sale_price: form.quote_only ? null : (form.sale_price ? Number(form.sale_price) : null),
        stock: Number(form.stock),
        images: form.images.filter(Boolean),
        spec_pdf_url: form.spec_pdf_url || null,
        manual_pdf_url: form.manual_pdf_url || null,
        long_description: (form.long_description || [])
          .filter((b) => b && (b.image_url || b.heading || b.heading_ar || b.body || b.body_ar))
          .map((b) => ({
            image_url: b.image_url || null,
            heading: b.heading || null,
            heading_ar: b.heading_ar || null,
            body: b.body || null,
            body_ar: b.body_ar || null,
            image_has_text: !!b.image_has_text,
            text_x: typeof b.text_x === "number" ? b.text_x : 50,
            text_y: typeof b.text_y === "number" ? b.text_y : 50,
          })),
        variants: (form.variants || []).map((v) => ({
          id: v.id || slugifyVariant(form.slug, v.btu, v.label),
          label: v.label,
          btu: String(v.btu || ""),
          price: form.quote_only ? 0 : Number(v.price || 0),
          sale_price: form.quote_only ? null : (v.sale_price ? Number(v.sale_price) : null),
          stock: Number(v.stock || 0),
        })),
        highlights: (form.highlights || [])
          .filter((h) => h && h.icon && (h.label || h.label_ar))
          .map((h) => ({
            icon: h.icon,
            label: h.label || "",
            label_ar: h.label_ar || null,
          })),
        home_sections: Array.isArray(form.home_sections)
          ? form.home_sections.filter(Boolean)
          : [],
        home_section_orders: Object.fromEntries(
          Object.entries(form.home_section_orders || {})
            .filter(([k]) => (form.home_sections || []).includes(k))
            .map(([k, v]) => [k, Number(v) || 0])
        ),
      };
      if (editing === "new") {
        await api.post("/admin/products", payload);
        toast.success("Product created");
      } else {
        await api.put(`/admin/products/${editing}`, payload);
        toast.success("Product updated");
      }
      setEditing(null);
      refresh();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  // Variant helpers
  const addVariant = () => {
    setForm((prev) => ({
      ...prev,
      variants: [
        ...(prev.variants || []),
        {
          id: "",
          label: "",
          btu: "",
          price: prev.price || 0,
          sale_price: null,
          stock: 0,
        },
      ],
    }));
  };
  const addPreset = (preset) => {
    setForm((prev) => ({
      ...prev,
      variants: [
        ...(prev.variants || []),
        {
          id: slugifyVariant(prev.slug, preset.btu, preset.label),
          label: preset.label,
          btu: preset.btu,
          price: prev.price || 0,
          sale_price: null,
          stock: 0,
        },
      ],
    }));
  };
  const updateVariant = (idx, patch) => {
    setForm((prev) => ({
      ...prev,
      variants: prev.variants.map((v, i) => (i === idx ? { ...v, ...patch } : v)),
    }));
  };
  const removeVariant = (idx) => {
    setForm((prev) => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== idx),
    }));
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Product deleted");
    refresh();
  };

  const handleDuplicate = async (p) => {
    try {
      const { data } = await api.post(`/admin/products/${p.id}/duplicate`);
      toast.success(`Duplicated · ${data.slug}`);
      await refresh();
      // Open the new copy in the editor so admin can tweak it immediately.
      startEdit(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Duplicate failed");
    }
  };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-slate-500">Catalog</div>
          <h1 className="mt-2 font-display font-extrabold text-4xl tracking-tight">Products</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={async () => {
              try {
                await downloadAuthFile("/admin/products/export.xlsx", "shamstrade-products.xlsx");
              } catch (err) {
                toast.error(formatApiError(err?.response?.data?.detail) || "Download failed");
              }
            }}
            data-testid="admin-export-xlsx"
            className="inline-flex items-center gap-2 h-11 px-4 rounded-full bg-ink text-white hover:bg-electric text-xs font-mono uppercase tracking-[0.2em]"
          >
            <Download size={14} /> Export Excel
          </button>
          <button
            type="button"
            onClick={async () => {
              try {
                await downloadAuthFile("/admin/products/export.csv", "shamstrade-products.csv");
              } catch (err) {
                toast.error(formatApiError(err?.response?.data?.detail) || "Download failed");
              }
            }}
            data-testid="admin-export-csv"
            className="inline-flex items-center gap-2 h-11 px-4 rounded-full bg-secondary hover:bg-ink hover:text-white text-xs font-mono uppercase tracking-[0.2em]"
          >
            <Download size={14} /> Export CSV
          </button>
          <label className="inline-flex items-center gap-2 h-11 px-4 rounded-full bg-secondary hover:bg-ink hover:text-white text-xs font-mono uppercase tracking-[0.2em] cursor-pointer">
            <Upload size={14} /> Import CSV
            <input
              data-testid="admin-import-csv"
              type="file"
              accept=".csv"
              className="hidden"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                const fd = new FormData();
                fd.append("file", f);
                try {
                  const { data } = await api.post("/admin/products/import", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                  });
                  toast.success(`Imported · ${data.inserted} new, ${data.updated} updated${data.errors?.length ? `, ${data.errors.length} errors` : ""}`);
                  refresh();
                } catch (err) {
                  toast.error("Import failed");
                }
                e.target.value = "";
              }}
            />
          </label>
          <button
            onClick={translateAllMissing}
            disabled={translatingAll}
            data-testid="admin-translate-missing"
            className="inline-flex items-center gap-2 h-11 px-4 rounded-full bg-secondary hover:bg-ink hover:text-white text-xs font-mono uppercase tracking-[0.2em] disabled:opacity-50 disabled:cursor-wait"
          >
            <Languages size={14} /> {translatingAll ? "Translating…" : "Translate missing AR"}
          </button>
          <button
            onClick={startNew}
            data-testid="admin-add-product"
            className="inline-flex items-center gap-2 h-11 px-4 rounded-full bg-ink text-white hover:bg-electric text-xs font-mono uppercase tracking-[0.2em]"
          >
            <Plus size={14} /> New product
          </button>
        </div>
      </div>

      <div className="mt-8 border border-black/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-left">
            <tr>
              <th className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest">Product</th>
              <th className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest">Brand</th>
              <th className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest">Type</th>
              <th className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest">Price</th>
              <th className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest">Stock</th>
              <th className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-t border-black/10">
                <td className="px-4 py-3 font-medium">{p.name}</td>
                <td className="px-4 py-3 font-mono">{p.brand}</td>
                <td className="px-4 py-3 font-mono uppercase">{p.specs?.type}</td>
                <td className="px-4 py-3 font-mono">{p.sale_price || p.price}</td>
                <td className="px-4 py-3 font-mono">{p.stock}</td>
                <td className="px-4 py-3 flex items-center gap-2">
                  <button
                    onClick={() => startEdit(p)}
                    data-testid={`admin-edit-${p.slug}`}
                    className="w-9 h-9 grid place-items-center border border-black/15 hover:bg-ink hover:text-white"
                    title="Edit"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => handleDuplicate(p)}
                    data-testid={`admin-duplicate-${p.slug}`}
                    className="w-9 h-9 grid place-items-center border border-black/15 hover:bg-electric hover:text-white"
                    title="Duplicate"
                  >
                    <Copy size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(p.id)}
                    data-testid={`admin-delete-${p.slug}`}
                    className="w-9 h-9 grid place-items-center border border-black/15 hover:bg-destructive hover:text-white"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4">
          <div className="bg-white border border-ink max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 shadow-brut">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-extrabold text-2xl">
                {editing === "new" ? "New product" : "Edit product"}
              </h2>
              <button
                onClick={() => setEditing(null)}
                className="text-xs font-mono uppercase tracking-widest hover:text-electric"
              >
                Close
              </button>
            </div>
            <form onSubmit={submit} className="mt-6 grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-mono uppercase tracking-widest">Slug</label>
                <input
                  required
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  className="mt-1 w-full h-10 px-3 border border-black/15 focus:border-electric focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest">Name (EN)</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 w-full h-10 px-3 border border-black/15 focus:border-electric focus:outline-none"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs font-mono uppercase tracking-widest">Name (AR)</label>
                  <button
                    type="button"
                    onClick={() => translateField("name", "name_ar", "product name")}
                    disabled={translating === "name_ar"}
                    data-testid="admin-translate-name"
                    className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-electric hover:underline disabled:opacity-50"
                  >
                    <Languages size={11} /> {translating === "name_ar" ? "…" : "Translate"}
                  </button>
                </div>
                <input
                  dir="rtl"
                  value={form.name_ar || ""}
                  onChange={(e) => setForm({ ...form, name_ar: e.target.value })}
                  data-testid="admin-product-name-ar"
                  placeholder="الاسم بالعربية"
                  className="mt-1 w-full h-10 px-3 border border-black/15 focus:border-electric focus:outline-none"
                  style={{ fontFamily: '"Cairo","Tajawal",sans-serif' }}
                />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest">Brand</label>
                <input
                  required
                  value={form.brand}
                  onChange={(e) => setForm({ ...form, brand: e.target.value })}
                  className="mt-1 w-full h-10 px-3 border border-black/15 focus:border-electric focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest">Category</label>
                <input
                  required
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="mt-1 w-full h-10 px-3 border border-black/15 focus:border-electric focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest">Price (JOD)</label>
                <input
                  required={!form.quote_only}
                  disabled={form.quote_only}
                  type="number"
                  step="0.01"
                  value={form.quote_only ? "" : form.price}
                  placeholder={form.quote_only ? "— Quote only —" : ""}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="mt-1 w-full h-10 px-3 border border-black/15 focus:border-electric focus:outline-none disabled:bg-secondary disabled:text-slate-400"
                />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest">Sale price</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.sale_price ?? ""}
                  onChange={(e) =>
                    setForm({ ...form, sale_price: e.target.value === "" ? null : e.target.value })
                  }
                  className="mt-1 w-full h-10 px-3 border border-black/15 focus:border-electric focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest">Type</label>
                <select
                  value={form.specs.type}
                  onChange={(e) => setForm({ ...form, specs: { ...form.specs, type: e.target.value } })}
                  className="mt-1 w-full h-10 px-3 border border-black/15 focus:border-electric focus:outline-none bg-white"
                >
                  <option value="split">split</option>
                  <option value="cassette">cassette</option>
                  <option value="floor">floor</option>
                  <option value="duct">duct</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest">BTU</label>
                <input
                  value={form.specs.btu}
                  onChange={(e) => setForm({ ...form, specs: { ...form.specs, btu: e.target.value } })}
                  className="mt-1 w-full h-10 px-3 border border-black/15 focus:border-electric focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest">Energy</label>
                <input
                  value={form.specs.energy_rating}
                  onChange={(e) =>
                    setForm({ ...form, specs: { ...form.specs, energy_rating: e.target.value } })
                  }
                  className="mt-1 w-full h-10 px-3 border border-black/15 focus:border-electric focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest">Color</label>
                <input
                  value={form.specs.color || ""}
                  onChange={(e) => setForm({ ...form, specs: { ...form.specs, color: e.target.value } })}
                  data-testid="admin-product-color"
                  placeholder="White"
                  className="mt-1 w-full h-10 px-3 border border-black/15 focus:border-electric focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest">Warranty</label>
                <input
                  value={form.specs.warranty || ""}
                  onChange={(e) => setForm({ ...form, specs: { ...form.specs, warranty: e.target.value } })}
                  data-testid="admin-product-warranty"
                  placeholder="3 years"
                  className="mt-1 w-full h-10 px-3 border border-black/15 focus:border-electric focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest">Stock</label>
                <input
                  required
                  type="number"
                  value={form.stock}
                  onChange={(e) => setForm({ ...form, stock: e.target.value })}
                  className="mt-1 w-full h-10 px-3 border border-black/15 focus:border-electric focus:outline-none"
                />
              </div>
              <div className="sm:col-span-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-mono uppercase tracking-widest">Product images</label>
                  <div className="text-[11px] text-slate-500">
                    {form.images.filter(Boolean).length} / up to 8 · first one is the main photo
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-4 sm:grid-cols-6 gap-2">
                  {form.images.filter(Boolean).map((url, idx) => (
                    <div
                      key={`${url}-${idx}`}
                      data-testid={`admin-product-image-${idx}`}
                      className={`relative aspect-square bg-secondary rounded-lg overflow-hidden border ${
                        idx === 0 ? "border-ink ring-2 ring-electric/40" : "border-black/10"
                      }`}
                    >
                      <img
                        src={resolveImg(url)}
                        alt=""
                        className="w-full h-full object-contain p-1"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                      {idx === 0 && (
                        <span className="absolute top-1 left-1 text-[9px] font-mono uppercase tracking-widest bg-ink text-white px-1.5 py-0.5 rounded">
                          Main
                        </span>
                      )}
                      <div className="absolute bottom-1 left-1 right-1 flex items-center justify-between gap-1">
                        {idx > 0 && (
                          <button
                            type="button"
                            onClick={() =>
                              setForm((prev) => {
                                const list = prev.images.filter(Boolean);
                                [list[idx - 1], list[idx]] = [list[idx], list[idx - 1]];
                                return { ...prev, images: list };
                              })
                            }
                            className="w-6 h-6 rounded bg-ink/80 text-white text-[10px] hover:bg-electric"
                            title="Move left"
                          >
                            ←
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() =>
                            setForm((prev) => ({
                              ...prev,
                              images: prev.images.filter((_, i) => i !== idx).filter(Boolean),
                            }))
                          }
                          className="ml-auto w-6 h-6 rounded bg-destructive/90 text-white text-[10px] hover:bg-destructive"
                          title="Remove"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                  {form.images.filter(Boolean).length < 8 && (
                    <label className="aspect-square bg-white border-2 border-dashed border-black/20 hover:border-ink rounded-lg grid place-items-center cursor-pointer text-slate-500 hover:text-ink text-center p-2">
                      <div>
                        <Upload size={18} className="mx-auto mb-1" />
                        <div className="text-[10px] font-mono uppercase tracking-widest leading-tight">
                          {uploading ? "Uploading…" : "Add photo"}
                        </div>
                      </div>
                      <input
                        data-testid="admin-product-image-upload"
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
                        className="hidden"
                        disabled={uploading}
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          if (f.size > 8 * 1024 * 1024) {
                            toast.error("Max file size is 8 MB");
                            return;
                          }
                          setUploading(true);
                          try {
                            const fd = new FormData();
                            fd.append("file", f);
                            const { data } = await api.post("/admin/upload", fd, {
                              headers: { "Content-Type": "multipart/form-data" },
                            });
                            setForm((prev) => ({
                              ...prev,
                              images: [...prev.images.filter(Boolean), data.url],
                            }));
                            toast.success("Image uploaded");
                          } catch (err) {
                            toast.error("Upload failed");
                          } finally {
                            setUploading(false);
                            e.target.value = "";
                          }
                        }}
                      />
                    </label>
                  )}
                </div>
                <input
                  placeholder="…or paste an image URL and press Enter"
                  data-testid="admin-product-image-url"
                  onKeyDown={(e) => {
                    if (e.key !== "Enter") return;
                    e.preventDefault();
                    const v = e.target.value.trim();
                    if (!v) return;
                    setForm((prev) => ({
                      ...prev,
                      images: [...prev.images.filter(Boolean), v].slice(0, 8),
                    }));
                    e.target.value = "";
                  }}
                  className="mt-2 w-full h-10 px-3 border border-black/15 focus:border-electric focus:outline-none text-sm"
                />
              </div>

              {/* ---- PDF uploads (datasheet + user manual) ---- */}
              {[
                { key: "spec_pdf_url", label: "Spec PDF (datasheet)", testid: "admin-product-spec-pdf" },
                { key: "manual_pdf_url", label: "User manual PDF", testid: "admin-product-manual-pdf" },
              ].map((pdf) => (
                <div key={pdf.key} className="sm:col-span-2">
                  <label className="text-xs font-mono uppercase tracking-widest">{pdf.label}</label>
                  <div className="mt-2 flex items-center gap-3 flex-wrap">
                    <label className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-ink text-white hover:bg-electric text-xs font-mono uppercase tracking-widest cursor-pointer">
                      <Upload size={13} /> Upload PDF
                      <input
                        data-testid={pdf.testid}
                        type="file"
                        accept="application/pdf,.pdf"
                        className="hidden"
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          if (f.size > 200 * 1024 * 1024) {
                            toast.error("Max file size is 200 MB");
                            return;
                          }
                          try {
                            const fd = new FormData();
                            fd.append("file", f);
                            const { data } = await api.post("/admin/upload-pdf", fd, {
                              headers: { "Content-Type": "multipart/form-data" },
                            });
                            setForm((prev) => ({ ...prev, [pdf.key]: data.url }));
                            toast.success("PDF uploaded");
                          } catch (err) {
                            toast.error(formatApiError(err?.response?.data?.detail) || "Upload failed");
                          } finally {
                            e.target.value = "";
                          }
                        }}
                      />
                    </label>
                    {form[pdf.key] ? (
                      <div className="flex items-center gap-2 text-xs font-mono">
                        <a
                          href={resolveImg(form[pdf.key])}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-electric underline truncate max-w-[260px]"
                        >
                          {form[pdf.key]}
                        </a>
                        <button
                          type="button"
                          onClick={() => setForm((prev) => ({ ...prev, [pdf.key]: "" }))}
                          className="text-destructive underline"
                        >
                          remove
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400 font-mono">No file attached</span>
                    )}
                  </div>
                </div>
              ))}

              {/* ---- Rich content blocks (Amazon-style photo+paragraph sections) ---- */}
              <div className="sm:col-span-2">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-xs font-mono uppercase tracking-widest">About this item</div>
                    <div className="text-[11px] text-slate-500 mt-1">
                      Optional rich content (image + heading + paragraph) shown below the buy box,
                      like Amazon's "From the manufacturer" section. Add as many rows as you want.
                    </div>
                  </div>
                  <button
                    type="button"
                    data-testid="admin-long-desc-add"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        long_description: [
                          ...(prev.long_description || []),
                          { image_url: "", heading: "", heading_ar: "", body: "", body_ar: "", image_has_text: false, text_x: 50, text_y: 50 },
                        ],
                      }))
                    }
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-secondary hover:bg-ink hover:text-white text-[11px] font-mono uppercase tracking-widest"
                  >
                    <Plus size={12} /> Add section
                  </button>
                </div>

                {(form.long_description || []).length === 0 && (
                  <div className="border border-dashed border-black/15 rounded-lg p-4 text-xs text-slate-500 font-mono">
                    No extra sections yet — click "Add section" to include an image + paragraph.
                  </div>
                )}

                <div className="space-y-3">
                  {(form.long_description || []).map((block, idx) => (
                    <div
                      key={idx}
                      data-testid={`admin-long-desc-row-${idx}`}
                      className="border border-black/10 rounded-lg p-3 bg-white"
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                          Section #{idx + 1}
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            disabled={idx === 0}
                            onClick={() =>
                              setForm((prev) => {
                                const list = [...prev.long_description];
                                [list[idx - 1], list[idx]] = [list[idx], list[idx - 1]];
                                return { ...prev, long_description: list };
                              })
                            }
                            className="text-[10px] font-mono uppercase tracking-widest text-slate-500 hover:text-ink disabled:opacity-30"
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            disabled={idx === (form.long_description.length - 1)}
                            onClick={() =>
                              setForm((prev) => {
                                const list = [...prev.long_description];
                                [list[idx + 1], list[idx]] = [list[idx], list[idx + 1]];
                                return { ...prev, long_description: list };
                              })
                            }
                            className="text-[10px] font-mono uppercase tracking-widest text-slate-500 hover:text-ink disabled:opacity-30"
                          >
                            ↓
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setForm((prev) => ({
                                ...prev,
                                long_description: prev.long_description.filter((_, i) => i !== idx),
                              }))
                            }
                            className="text-[10px] font-mono uppercase tracking-widest text-destructive hover:underline ml-2"
                          >
                            Remove
                          </button>
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-[180px_1fr] gap-3">
                        <div className="space-y-2">
                          <div className="w-full aspect-[4/3] bg-secondary rounded-lg overflow-hidden grid place-items-center border border-black/10">
                            {block.image_url ? (
                              <img
                                src={resolveImg(block.image_url)}
                                alt=""
                                className="w-full h-full object-contain"
                                onError={(e) => { e.currentTarget.style.display = "none"; }}
                              />
                            ) : (
                              <ImageIcon size={24} className="text-slate-400" />
                            )}
                          </div>
                          <label className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-ink text-white hover:bg-electric text-[10px] font-mono uppercase tracking-widest cursor-pointer w-full justify-center">
                            <Upload size={12} /> Upload
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
                              className="hidden"
                              onChange={async (e) => {
                                const f = e.target.files?.[0];
                                if (!f) return;
                                try {
                                  const fd = new FormData();
                                  fd.append("file", f);
                                  const { data } = await api.post("/admin/upload", fd, {
                                    headers: { "Content-Type": "multipart/form-data" },
                                  });
                                  setForm((prev) => {
                                    const list = [...prev.long_description];
                                    list[idx] = { ...list[idx], image_url: data.url };
                                    return { ...prev, long_description: list };
                                  });
                                  toast.success("Image uploaded");
                                } catch (err) {
                                  toast.error(formatApiError(err?.response?.data?.detail) || "Upload failed");
                                } finally {
                                  e.target.value = "";
                                }
                              }}
                            />
                          </label>
                          {block.image_url && (
                            <button
                              type="button"
                              onClick={() =>
                                setForm((prev) => {
                                  const list = [...prev.long_description];
                                  list[idx] = { ...list[idx], image_url: "" };
                                  return { ...prev, long_description: list };
                                })
                              }
                              className="text-[10px] text-destructive underline font-mono w-full text-center"
                            >
                              Remove image
                            </button>
                          )}
                          {/* Per-block: text overlay toggle, drag-position pin, AI suggest button */}
                          <BlockImageOverlayEditor
                            block={block}
                            onChange={(patch) =>
                              setForm((prev) => {
                                const list = [...prev.long_description];
                                list[idx] = { ...list[idx], ...patch };
                                return { ...prev, long_description: list };
                              })
                            }
                          />
                        </div>

                        <div className="space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              placeholder="Heading (EN)"
                              value={block.heading || ""}
                              onChange={(e) =>
                                setForm((prev) => {
                                  const list = [...prev.long_description];
                                  list[idx] = { ...list[idx], heading: e.target.value };
                                  return { ...prev, long_description: list };
                                })
                              }
                              className="h-9 px-3 border border-black/15 focus:border-electric focus:outline-none text-sm"
                            />
                            <input
                              placeholder="العنوان (AR)"
                              dir="rtl"
                              value={block.heading_ar || ""}
                              onChange={(e) =>
                                setForm((prev) => {
                                  const list = [...prev.long_description];
                                  list[idx] = { ...list[idx], heading_ar: e.target.value };
                                  return { ...prev, long_description: list };
                                })
                              }
                              className="h-9 px-3 border border-black/15 focus:border-electric focus:outline-none text-sm"
                            />
                          </div>
                          <textarea
                            placeholder="Paragraph (EN)"
                            rows={3}
                            value={block.body || ""}
                            onChange={(e) =>
                              setForm((prev) => {
                                const list = [...prev.long_description];
                                list[idx] = { ...list[idx], body: e.target.value };
                                return { ...prev, long_description: list };
                              })
                            }
                            className="w-full px-3 py-2 border border-black/15 focus:border-electric focus:outline-none text-sm"
                          />
                          <textarea
                            placeholder="النص (AR)"
                            dir="rtl"
                            rows={3}
                            value={block.body_ar || ""}
                            onChange={(e) =>
                              setForm((prev) => {
                                const list = [...prev.long_description];
                                list[idx] = { ...list[idx], body_ar: e.target.value };
                                return { ...prev, long_description: list };
                              })
                            }
                            className="w-full px-3 py-2 border border-black/15 focus:border-electric focus:outline-none text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Highlights / feature-icon picker */}
              <div className="sm:col-span-2 bg-secondary/50 rounded-lg p-4 border border-black/5">
                <HighlightsPicker
                  value={form.highlights || []}
                  onChange={(next) => setForm((prev) => ({ ...prev, highlights: next }))}
                />
              </div>

              {/* Home-page placement — admin manually pins this product to specific home strips */}
              <div className="sm:col-span-2 bg-secondary/50 rounded-lg p-4 border border-black/5">
                <div className="text-xs font-mono uppercase tracking-widest text-slate-700 mb-2">
                  Show on home page
                </div>
                <p className="text-[11px] text-slate-500 font-mono mb-3">
                  Pick where this product appears on the home page. Leave all unchecked to use auto rules. The number next to each section sets the sort position (lower = appears first).
                </p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {[
                    { key: "new_arrivals", label: "New Arrivals" },
                    { key: "best_sellers", label: "Best Sellers" },
                    { key: "light_commercial", label: "Light Commercial" },
                    { key: "deals", label: "Deals of the week" },
                  ].map((s) => {
                    const checked = (form.home_sections || []).includes(s.key);
                    const pos = (form.home_section_orders || {})[s.key] ?? "";
                    return (
                      <div
                        key={s.key}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-display transition-colors ${
                          checked
                            ? "bg-electric/10 border-electric text-ink"
                            : "bg-white border-black/10"
                        }`}
                        data-testid={`home-section-${s.key}`}
                      >
                        <label className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer">
                          <input
                            type="checkbox"
                            className="w-4 h-4 accent-electric"
                            checked={checked}
                            onChange={(e) =>
                              setForm((prev) => {
                                const cur = new Set(prev.home_sections || []);
                                const orders = { ...(prev.home_section_orders || {}) };
                                if (e.target.checked) {
                                  cur.add(s.key);
                                } else {
                                  cur.delete(s.key);
                                  delete orders[s.key];
                                }
                                return {
                                  ...prev,
                                  home_sections: Array.from(cur),
                                  home_section_orders: orders,
                                };
                              })
                            }
                          />
                          <span className="truncate">{s.label}</span>
                        </label>
                        {checked && (
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={pos}
                            placeholder="#"
                            data-testid={`home-section-pos-${s.key}`}
                            onChange={(e) =>
                              setForm((prev) => ({
                                ...prev,
                                home_section_orders: {
                                  ...(prev.home_section_orders || {}),
                                  [s.key]: e.target.value,
                                },
                              }))
                            }
                            className="w-14 h-8 px-2 text-center text-xs font-mono rounded border border-black/15 bg-white focus:border-electric focus:outline-none"
                            title="Sort position (lower = first)"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="sm:col-span-2">
                <label className="text-xs font-mono uppercase tracking-widest">Description (EN)</label>
                <textarea
                  required
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border border-black/15 focus:border-electric focus:outline-none"
                />
              </div>
              <div className="sm:col-span-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-mono uppercase tracking-widest">Description (AR)</label>
                  <button
                    type="button"
                    onClick={() => translateField("description", "description_ar", "product description paragraph")}
                    disabled={translating === "description_ar"}
                    data-testid="admin-translate-description"
                    className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-electric hover:underline disabled:opacity-50"
                  >
                    <Languages size={11} /> {translating === "description_ar" ? "…" : "Translate"}
                  </button>
                </div>
                <textarea
                  dir="rtl"
                  rows={3}
                  value={form.description_ar || ""}
                  onChange={(e) => setForm({ ...form, description_ar: e.target.value })}
                  data-testid="admin-product-description-ar"
                  placeholder="الوصف بالعربية"
                  className="mt-1 w-full px-3 py-2 border border-black/15 focus:border-electric focus:outline-none"
                  style={{ fontFamily: '"Cairo","Tajawal",sans-serif' }}
                />
              </div>

              {/* VARIANTS / SIZES editor */}
              <div className="sm:col-span-2 border border-black/10 rounded-lg p-4 bg-secondary/40">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="text-xs font-mono uppercase tracking-widest">Sizes / Variants</div>
                    <div className="mt-1 text-[11px] text-slate-500">
                      Add per-size pricing & stock (e.g. 1 Ton, 1.5 Ton, 2 Ton). Leave empty to use the base price/stock above.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={addVariant}
                    data-testid="admin-add-variant"
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-ink text-white hover:bg-electric text-[11px] font-mono uppercase tracking-widest"
                  >
                    <Plus size={12} /> Add custom
                  </button>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 self-center mr-1">Quick add:</span>
                  {SIZE_PRESETS.map((p) => (
                    <button
                      key={p.label}
                      type="button"
                      onClick={() => addPreset(p)}
                      data-testid={`admin-preset-${p.btu}`}
                      className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-white border border-black/15 hover:bg-ink hover:text-white text-[10px] font-mono uppercase tracking-wider"
                    >
                      {p.label} · {p.btu}
                    </button>
                  ))}
                </div>

                {form.variants && form.variants.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <div className="hidden sm:grid grid-cols-12 gap-2 text-[9px] font-mono uppercase tracking-widest text-slate-500 px-1">
                      <div className="col-span-3">Label</div>
                      <div className="col-span-2">BTU</div>
                      <div className="col-span-2">Price</div>
                      <div className="col-span-2">Sale</div>
                      <div className="col-span-2">Stock</div>
                      <div className="col-span-1"></div>
                    </div>
                    {form.variants.map((v, idx) => (
                      <div
                        key={v.id || idx}
                        data-testid={`admin-variant-row-${idx}`}
                        className="grid grid-cols-12 gap-2 items-center"
                      >
                        <input
                          placeholder="1.5 Ton"
                          value={v.label}
                          onChange={(e) => updateVariant(idx, { label: e.target.value })}
                          data-testid={`admin-variant-label-${idx}`}
                          className="col-span-3 h-9 px-2 border border-black/15 rounded text-sm bg-white focus:border-electric focus:outline-none"
                        />
                        <input
                          placeholder="18000"
                          value={v.btu}
                          onChange={(e) => updateVariant(idx, { btu: e.target.value })}
                          data-testid={`admin-variant-btu-${idx}`}
                          className="col-span-2 h-9 px-2 border border-black/15 rounded text-sm bg-white focus:border-electric focus:outline-none"
                        />
                        <input
                          type="number"
                          step="0.01"
                          placeholder="480"
                          value={v.price ?? ""}
                          onChange={(e) => updateVariant(idx, { price: e.target.value })}
                          data-testid={`admin-variant-price-${idx}`}
                          className="col-span-2 h-9 px-2 border border-black/15 rounded text-sm bg-white focus:border-electric focus:outline-none"
                        />
                        <input
                          type="number"
                          step="0.01"
                          placeholder="—"
                          value={v.sale_price ?? ""}
                          onChange={(e) => updateVariant(idx, { sale_price: e.target.value === "" ? null : e.target.value })}
                          data-testid={`admin-variant-sale-${idx}`}
                          className="col-span-2 h-9 px-2 border border-black/15 rounded text-sm bg-white focus:border-electric focus:outline-none"
                        />
                        <input
                          type="number"
                          placeholder="0"
                          value={v.stock ?? 0}
                          onChange={(e) => updateVariant(idx, { stock: e.target.value })}
                          data-testid={`admin-variant-stock-${idx}`}
                          className="col-span-2 h-9 px-2 border border-black/15 rounded text-sm bg-white focus:border-electric focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => removeVariant(idx)}
                          data-testid={`admin-variant-remove-${idx}`}
                          aria-label="Remove variant"
                          className="col-span-1 h-9 w-9 grid place-items-center rounded bg-white border border-black/15 hover:bg-destructive hover:text-white"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* /VARIANTS */}

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                />
                Featured
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.on_sale}
                  onChange={(e) => setForm({ ...form, on_sale: e.target.checked })}
                />
                On sale
              </label>
              <label
                className="flex items-center gap-2 text-sm sm:col-span-2 p-3 border border-dashed border-black/20 bg-secondary/60"
                title="Hide price and show a 'Request a quote' CTA instead"
              >
                <input
                  data-testid="admin-product-quote-only"
                  type="checkbox"
                  checked={!!form.quote_only}
                  onChange={(e) => setForm({ ...form, quote_only: e.target.checked })}
                />
                <span>
                  <span className="font-semibold">Price on request (quote only)</span>
                  <span className="block text-xs text-slate-500">
                    When enabled, storefront hides the price and replaces Add-to-Cart with a "Request a quote" WhatsApp button.
                  </span>
                </span>
              </label>

              {error && (
                <div className="sm:col-span-2 text-sm font-mono text-destructive">{error}</div>
              )}

              <div className="sm:col-span-2 flex gap-3 mt-4">
                <button
                  type="submit"
                  data-testid="admin-product-save"
                  className="h-11 px-6 bg-ink text-white hover:bg-electric text-xs font-mono uppercase tracking-[0.2em]"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(null)}
                  className="h-11 px-6 border border-ink text-xs font-mono uppercase tracking-[0.2em]"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
